import { LoaderCircle, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import {
  formatearCantidadBodega,
  formatearFechaBodega,
  formatearTipoMovimiento,
} from '../../utils/bodega'

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-medium text-slate-800">
        {valor || '—'}
      </p>
    </div>
  )
}

function DetalleMovimientoBodegaModal({
  abierto,
  movimiento,
  onCerrar,
}) {
  const { empresaActual } = useEmpresa()

  const [detalles, setDetalles] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape') {
        onCerrar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, onCerrar])

  useEffect(() => {
    if (!abierto || !movimiento?.id || !empresaActual?.id) return

    cargarDetalles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, movimiento?.id, empresaActual?.id])

  const cargarDetalles = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('movimientos_bodega_detalle')
        .select(`
          id,
          producto_id,
          cantidad,
          producto:productos!fk_detalle_producto_empresa(
            codigo,
            nombre,
            unidad:unidades_medida!fk_producto_unidad_empresa(
              codigo,
              nombre
            )
          )
        `)
        .eq('movimiento_id', movimiento.id)
        .eq('empresa_id', empresaActual.id)

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setDetalles(data || [])
    } catch (errorCarga) {
      console.error(
        'Error al cargar el detalle del movimiento:',
        errorCarga
      )

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar el detalle del movimiento.'
      )
      setDetalles([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  if (!abierto || !movimiento) return null

  const alHacerClicFuera = (event) => {
    if (event.target === event.currentTarget) {
      onCerrar?.()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detalle-movimiento-titulo"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <div>
            <h2
              id="detalle-movimiento-titulo"
              className="text-xl font-bold text-slate-900"
            >
              Movimiento de bodega
            </h2>

            <span className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {formatearTipoMovimiento(movimiento.tipo_movimiento)}
            </span>
          </div>

          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-8 grid grid-cols-1 gap-x-6 gap-y-4 border-b border-slate-100 pb-8 sm:grid-cols-2">
            <Dato
              etiqueta="Fecha"
              valor={formatearFechaBodega(movimiento.fecha)}
            />

            <Dato
              etiqueta="Bodega origen"
              valor={
                movimiento.bodega_origen
                  ? `${movimiento.bodega_origen.codigo} — ${movimiento.bodega_origen.nombre}`
                  : '—'
              }
            />

            <Dato
              etiqueta="Bodega destino"
              valor={
                movimiento.bodega_destino
                  ? `${movimiento.bodega_destino.codigo} — ${movimiento.bodega_destino.nombre}`
                  : '—'
              }
            />

            <Dato
              etiqueta="Documento"
              valor={movimiento.tipo_documento}
            />

            <Dato
              etiqueta="N.º documento"
              valor={movimiento.numero_documento}
            />

            <Dato
              etiqueta="Referencia"
              valor={movimiento.referencia}
            />

            <div className="sm:col-span-2">
              <Dato
                etiqueta="Observaciones"
                valor={movimiento.observaciones}
              />
            </div>
          </div>

          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Productos
          </h3>

          {cargando ? (
            <div className="flex items-center justify-center py-10">
              <LoaderCircle
                size={28}
                className="animate-spin text-emerald-600"
              />
            </div>
          ) : error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </p>
          ) : detalles.length === 0 ? (
            <p className="text-sm text-slate-500">
              Este movimiento no tiene productos registrados.
            </p>
          ) : (
            <div className="w-full overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Código</th>
                    <th className="px-4 py-2.5">Producto</th>
                    <th className="px-4 py-2.5">Unidad</th>
                    <th className="px-4 py-2.5 text-right">
                      Cantidad
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {detalles.map((detalle) => (
                    <tr key={detalle.id}>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {detalle.producto?.codigo || '—'}
                      </td>

                      <td className="px-4 py-3 text-slate-700">
                        {detalle.producto?.nombre || '—'}
                      </td>

                      <td className="px-4 py-3 text-slate-600">
                        {detalle.producto?.unidad?.codigo || '—'}
                      </td>

                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {formatearCantidadBodega(detalle.cantidad)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 p-6">
          <button
            type="button"
            onClick={onCerrar}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 sm:w-auto"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default DetalleMovimientoBodegaModal
